<template>
  <div class="aap-page">
    <div class="aap-top">
      <h1>提領申請管理</h1>
    </div>

    <!-- 篩選 -->
    <div class="aap-filters">
      <button v-for="s in statusOptions" :key="s.val"
              class="aap-filter-btn" :class="{ active: filterStatus === s.val }"
              @click="filterStatus = s.val">{{ s.label }}</button>
    </div>

    <div class="aap-table-wrap">
      <table class="aap-table">
        <thead>
          <tr>
            <th>合作夥伴</th>
            <th>申請金額</th>
            <th>銀行代碼</th>
            <th>帳號</th>
            <th>戶名</th>
            <th>狀態</th>
            <th>申請時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in filteredList" :key="p.id">
            <td>
              <div class="aap-name">{{ p.affiliate_name }}</div>
              <div class="aap-email">{{ p.affiliate_email }}</div>
            </td>
            <td class="aap-amt">NT$ {{ fmtNum(p.amount) }}</td>
            <td>{{ p.bank_code || '—' }}</td>
            <td>{{ p.bank_account || '—' }}</td>
            <td>{{ p.bank_holder || '—' }}</td>
            <td><span class="aap-badge" :class="p.status">{{ statusLabel(p.status) }}</span></td>
            <td>{{ fmtDate(p.applied_at) }}</td>
            <td>
              <div class="aap-actions">
                <template v-if="p.status === 'pending'">
                  <button class="aap-btn-sm primary" @click="markPaid(p)">標記已匯款</button>
                  <button class="aap-btn-sm danger" @click="rejectPayout(p)">拒絕</button>
                </template>
                <span v-else-if="p.status === 'paid'" class="aap-done">已完成 {{ fmtDate(p.paid_at) }}</span>
                <span v-else class="aap-rejected">{{ p.admin_note || '已拒絕' }}</span>
              </div>
            </td>
          </tr>
          <tr v-if="filteredList.length === 0">
            <td colspan="8" class="aap-empty">無提領申請</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'

const payouts = ref([])
const filterStatus = ref('all')

const statusOptions = [
  { val: 'all', label: '全部' },
  { val: 'pending', label: '待處理' },
  { val: 'paid', label: '已匯款' },
  { val: 'rejected', label: '已拒絕' },
]

const filteredList = computed(() => {
  if (filterStatus.value === 'all') return payouts.value
  return payouts.value.filter(p => p.status === filterStatus.value)
})

async function load() {
  try {
    const res = await axios.get('/api/admin/affiliate/affiliate-payouts', { withCredentials: true })
    if (res.data.success) payouts.value = res.data.payouts
  } catch (e) { console.error(e) }
}

async function markPaid(p) {
  if (!confirm(`確認已將 NT$${fmtNum(p.amount)} 匯款給 ${p.affiliate_name}？`)) return
  try {
    const res = await axios.put(`/api/admin/affiliate/affiliate-payouts/${p.id}/pay`, {}, { withCredentials: true })
    if (res.data.success) await load()
  } catch (e) { alert('操作失敗') }
}

async function rejectPayout(p) {
  const note = window.prompt(`拒絕 ${p.affiliate_name} 的提領申請，請輸入原因（選填）：`)
  if (note === null) return
  try {
    const res = await axios.put(`/api/admin/affiliate/affiliate-payouts/${p.id}/reject`, { note }, { withCredentials: true })
    if (res.data.success) await load()
  } catch (e) { alert('操作失敗') }
}

function statusLabel(s) {
  const map = { pending: '待處理', paid: '已匯款', rejected: '已拒絕' }
  return map[s] || s
}
function fmtNum(n) { return Number(n || 0).toLocaleString('zh-TW') }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString('zh-TW') : '-' }

onMounted(load)
</script>

<style scoped>
* { box-sizing: border-box; }
.aap-page { padding: 1.5rem; }
.aap-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.2rem; }
.aap-top h1 { font-size: 1.3rem; color: #2E3A59; margin: 0; }

.aap-filters { display: flex; gap: 0.5rem; margin-bottom: 1rem; flex-wrap: wrap; }
.aap-filter-btn {
  border: 1px solid #ddd; background: #fff; border-radius: 20px;
  padding: 0.3rem 0.9rem; font-size: 0.83rem; cursor: pointer;
}
.aap-filter-btn.active { background: #2E3A59; color: #fff; border-color: #2E3A59; }

.aap-table-wrap { overflow-x: auto; }
.aap-table {
  width: 100%; border-collapse: collapse; font-size: 0.87rem;
  background: #fff; border-radius: 10px; overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.aap-table th {
  background: #f0f2f7; padding: 0.7rem 0.8rem; text-align: left;
  font-weight: 600; color: #555; white-space: nowrap;
}
.aap-table td { padding: 0.65rem 0.8rem; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
.aap-table tr:last-child td { border-bottom: none; }

.aap-name { font-weight: 600; color: #333; }
.aap-email { font-size: 0.78rem; color: #888; }
.aap-amt { font-weight: 700; color: #27ae60; }

.aap-badge {
  display: inline-block; padding: 0.2rem 0.6rem; border-radius: 12px;
  font-size: 0.78rem; font-weight: 600;
}
.aap-badge.pending { background: #fef9e7; color: #e67e22; }
.aap-badge.paid { background: #eafaf1; color: #27ae60; }
.aap-badge.rejected { background: #fdedec; color: #c0392b; }

.aap-actions { display: flex; gap: 0.4rem; flex-wrap: wrap; align-items: center; }
.aap-btn-sm {
  border: none; border-radius: 6px; padding: 0.3rem 0.7rem;
  cursor: pointer; font-size: 0.8rem; white-space: nowrap;
}
.aap-btn-sm.primary { background: #27ae60; color: #fff; }
.aap-btn-sm.primary:hover { background: #219a52; }
.aap-btn-sm.danger { background: #e74c3c; color: #fff; }
.aap-btn-sm.danger:hover { background: #c0392b; }
.aap-done { font-size: 0.8rem; color: #27ae60; }
.aap-rejected { font-size: 0.8rem; color: #c0392b; }
.aap-empty { text-align: center; color: #aaa; padding: 2rem; }
</style>
